/**
 * manager-product.js
 * Gerencia quick-add ao carrinho nos cards de produto.
 * Disponível como window.ProductManager.
 *
 * Dependências (devem carregar antes):
 *   - window.CartManager    (manager-cart.js)
 *   - window.ButtonFeedback (manager-global-button-feedback.js)
 *
 * ─── HTML esperado (.productCard) ─────────────────────────────────────────
 *
 *   <article class="productCard">
 *
 *     <!-- Botão que abre/fecha o popup de tamanhos -->
 *     <button data-product-add-toggle aria-expanded="false">
 *       Adicionar
 *     </button>
 *
 *     <!-- Popup de tamanhos -->
 *     <div data-size-popup hidden>
 *       <button class="productCard__sizeOption"
 *               data-size-option
 *               data-variant-id="12345678">P</button>
 *       <button class="productCard__sizeOption"
 *               data-size-option
 *               data-variant-id="12345679">M</button>
 *     </div>
 *
 *   </article>
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   .productCard               → container do card (escopo do popup)
 *   data-product-add-toggle    → abre/fecha popup
 *   data-size-popup            → container do popup
 *   data-size-option           → opção de tamanho (lê data-variant-id)
 *   data-variant-id            → ID da variante a adicionar
 *
 * ─── Comportamento ────────────────────────────────────────────────────────
 *
 *   - Click em data-product-add-toggle → abre popup do card / fecha outros
 *   - Click em data-size-option → CartManager.add() + fecha popup
 *   - Click fora de qualquer popup → fecha todos
 *   - Funciona com cards dinâmicos (event delegation em document)
 */

class ProductManager {
  constructor() {
    this._onClick = this._onClick.bind(this);
    document.addEventListener('click', this._onClick);
  }

  // ─── Event delegation ─────────────────────────────────────────────────────

  _onClick(e) {
    // Toggle popup
    const toggleBtn = e.target.closest('[data-product-add-toggle]');
    if (toggleBtn) {
      e.stopPropagation();
      const card   = toggleBtn.closest('.productCard');
      if (!card) return;
      const isOpen = card.querySelector('[data-size-popup]')?.classList.contains('is-open');
      this._closeAllPopups();
      if (!isOpen) this._openPopup(card);
      return;
    }

    // Selecionar tamanho → add to cart
    const sizeOption = e.target.closest('[data-size-option]');
    if (sizeOption) {
      const variantId = sizeOption.dataset.variantId;
      if (variantId) this._addToCart(sizeOption, variantId);
      return;
    }

    // Fechar ao clicar fora
    if (!e.target.closest('[data-size-popup]') && !e.target.closest('[data-product-add-toggle]')) {
      this._closeAllPopups();
    }
  }

  // ─── Popup ────────────────────────────────────────────────────────────────

  _openPopup(card) {
    const popup     = card.querySelector('[data-size-popup]');
    const toggleBtn = card.querySelector('[data-product-add-toggle]');
    if (!popup) return;
    popup.classList.add('is-open');
    popup.removeAttribute('hidden');
    toggleBtn?.setAttribute('aria-expanded', 'true');
  }

  _closePopup(card) {
    const popup     = card.querySelector('[data-size-popup]');
    const toggleBtn = card.querySelector('[data-product-add-toggle]');
    if (!popup) return;
    popup.classList.remove('is-open');
    popup.setAttribute('hidden', '');
    toggleBtn?.setAttribute('aria-expanded', 'false');
  }

  _closeAllPopups() {
    document.querySelectorAll('.productCard [data-size-popup].is-open').forEach(popup => {
      const card = popup.closest('.productCard');
      if (card) this._closePopup(card);
    });
  }

  // ─── Add to cart ──────────────────────────────────────────────────────────

  async _addToCart(btn, variantId) {
    // Congela as dimensões do botão para evitar layout shift ao injetar SVG
    btn.style.width  = `${btn.offsetWidth}px`;
    btn.style.height = `${btn.offsetHeight}px`;

    const result = await window.CartManager?.add(variantId, { button: btn });

    btn.style.width  = '';
    btn.style.height = '';

    if (result?.success) {
      const card = btn.closest('.productCard');
      if (card) this._closePopup(card);
    }
  }

  destroy() {
    document.removeEventListener('click', this._onClick);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.ProductManager = new ProductManager();
});
